import { addError } from "@wayflyer/blackbox-fe";
import { axiosClient } from "@wayflyer/auth-sdk";
import { isAxiosError } from "axios";
import Cookies from "js-cookie";

// Plain strings rather than react-intl descriptors: this module is not React, so it has no `intl` to format with.
// Both previously went through the Django gettext catalog, which had no entry for either, so it returned the lookup
// key and users saw "fetch network error" verbatim.
const NETWORK_ERROR_MESSAGE = "Network error";
const SERVER_ERROR_MESSAGE = "Internal server error";

/**
 * `isNetworkFailure` is true only when the browser dropped the request before the server responded. Nothing
 * reached the backend, so callers should not report it as an application error.
 */
export type RequestFailureHandler = (failureMessage: string, isNetworkFailure: boolean) => any;

/**
 *  @deprecated Use `useAxios` from `@wayflyer/auth-sdk` instead
 */
function executeRequest(
  method: string,
  url: RequestInfo,
  requestContentType: "multipart/form-data" | "application/json",
  requestData: any
) {
  const headers = {
    Accept: "application/json",
    "Cache-Control": "no-cache",
  };
  let data = requestData;
  if (requestData != null && requestContentType === "multipart/form-data") {
    data = new FormData();
    Object.keys(requestData).forEach((fieldName) => data.append(fieldName, requestData[fieldName]));
  }

  return axiosClient.request({
    url: typeof url === "string" ? url : url.url,
    method,
    headers: { ...headers, "Content-Type": requestContentType === "application/json" ? requestContentType : undefined },
    data,
    withCredentials: true,
    validateStatus: (status) => (status >= 200 && status < 300) || status === 400,
  });
}

function isNetworkError(e: unknown) {
  return isAxiosError(e) && e.code === "ERR_NETWORK" && !e.response;
}

/**
 *  @deprecated Use `useAxios` from `@wayflyer/auth-sdk` instead
 */
function handleRequestException(e, onFailure: RequestFailureHandler) {
  if (isNetworkError(e)) {
    onFailure(NETWORK_ERROR_MESSAGE, true);
  } else {
    addError(e);
    onFailure(SERVER_ERROR_MESSAGE, false);
  }
}

/**
 *  @deprecated Use `useAxios` from `@wayflyer/auth-sdk` instead
 */
function requestAndHandle(
  method: string,
  url: RequestInfo,
  requestContentType: "multipart/form-data" | "application/json",
  requestData: any,
  onSuccess: (responseData: any) => any,
  onFailure: RequestFailureHandler
) {
  executeRequest(method, url, requestContentType, requestData)
    .then((response) => {
      const json = response.data;
      if (response.status >= 200 && response.status < 300) {
        onSuccess(json);
      } else if (json && json.message) {
        // when the server responds with a 400 status and includes a JSON response with a "message", that's the error will display to the user.
        onFailure(json.message, false);
        if (json.errors) {
          addError(json.errors);
        }
      } else {
        throw Error(`Request failed with status ${response.status}`);
      }
    })
    .catch((e) => {
      if (e.message.includes("403")) {
        const cookies = document.cookie.split(";").map((v) => v.trim());
        const csrfTokens = cookies.filter((cookie) => cookie.includes("csrftoken"));
        if (csrfTokens.length > 1) {
          addError(new Error("403 Hit, more than one CSRF tokens found!"));
          Cookies.remove("csrftoken");
          window.location.href = "/";
        }
      }
      handleRequestException(e, onFailure);
    });
}

/**
 *  @deprecated Use `useAxios` from `@wayflyer/auth-sdk` instead
 */
export function post(
  url: RequestInfo,
  requestData: any,
  onSuccess: (responseData: any) => any,
  onFailure: RequestFailureHandler,
  requestContentType?: "multipart/form-data" | "application/json"
) {
  requestAndHandle(
    "POST",
    url,
    requestContentType != null ? requestContentType : "application/json",
    requestData,
    onSuccess,
    onFailure
  );
}

/**
 *  @deprecated Use `useAxios` from `@wayflyer/auth-sdk` instead
 */
export function get(url: RequestInfo, onSuccess: (responseData: any) => any, onFailure: RequestFailureHandler) {
  requestAndHandle("GET", url, "application/json", null, onSuccess, onFailure);
}

/**
 *  @deprecated Use `useAxios` from `@wayflyer/auth-sdk` instead
 */
export function requestPost(
  url: RequestInfo,
  requestData: any,
  requestContentType?: "multipart/form-data" | "application/json"
): Promise<any> {
  return new Promise((resolve, reject) => {
    post(
      url,
      requestData,
      (responseData) => resolve(responseData),
      (failureMessage) => reject(failureMessage),
      requestContentType
    );
  });
}
